/**
 * The next-step banner on "My inventions" (figures S4 / S4b, top of the page).
 *
 *   NEXT AT MY INVENTIONS · REVIEW OVERLAP
 *   Two of your inventions look like the same work — strongest pair 78.
 *   from your overlap scan · computed over everything you're entitled to see …
 *                                              [ Open the group → ]
 *                                              Snooze ▾ · Not now
 *   2 more items in your queue — See queue · 1 snoozed · show
 *
 * Presentational: the queue and the snooze record come in as props, so the
 * whole thing is testable with real functions and a real captured payload and
 * no module mock. The hook next door (useOwnNextStep) does the fetching.
 *
 * Snooze and "Not now" are REAL and DURABLE — they write the caller's own
 * `next_step_snoozed` preference through PUT /innovateflow/preferences, the
 * same 4-layer store Settings uses. Nothing here is a placeholder control.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { NextStepBanner } from '../spine'
import { SNOOZE_DAY_OPTIONS, type OwnNextStepItem, type OwnNextStepQueue } from './ownNextStep'

export interface OwnNextStepBannerProps {
  queue: OwnNextStepQueue
  /** Snooze this item for N days. Absent while the preference store is still
   * loading — the controls then say so instead of accepting a dead click. */
  onSnooze?: (item: OwnNextStepItem, days: number) => void
  /** "Not now" — drop it from the queue until the situation changes. */
  onDrop?: (item: OwnNextStepItem) => void
  /** Un-snooze, from the revealed snoozed list. */
  onRestore?: (item: OwnNextStepItem) => void
  /** True while the caller's own queue payloads are still in flight. */
  loading?: boolean
}

export default function OwnNextStepBanner({
  queue,
  onSnooze,
  onDrop,
  onRestore,
  loading = false,
}: OwnNextStepBannerProps) {
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false)
  const [showSnoozed, setShowSnoozed] = useState(false)
  // The figure shows the COUNT of the rest of the queue, not the queue itself.
  // "See queue" opens it here rather than linking to a page that does not
  // exist — a real control, and nothing on screen the figure does not have.
  const [showQueue, setShowQueue] = useState(false)

  const [first, ...rest] = queue.waiting
  const snoozedCount = queue.snoozed.length

  if (loading) {
    return (
      <div
        data-part="OwnNextStepBanner"
        className="mb-4 rounded-xl border border-iflow-500/25 border-l-4 border-l-iflow-500 bg-iflow-50/70 px-4 py-3 text-[13px] text-gray-600"
      >
        Working out what needs you first — reading your overlap, funding and topology scans.
      </div>
    )
  }

  if (!first) {
    return (
      <div data-part="OwnNextStepBanner">
        <NextStepBanner
          at="MY INVENTIONS"
          verb="nothing waiting"
          none
          headline={
            snoozedCount > 0
              ? `Nothing is waiting on you across your inventions right now — ${snoozedCount === 1 ? 'one item is' : `${snoozedCount} items are`} snoozed.`
              : 'Nothing is waiting on you across your inventions right now.'
          }
          rule="from your overlap, funding and topology scans · this is what they actually found, not a quiet failure"
          queue={
            snoozedCount > 0 ? (
              <SnoozedRow
                count={snoozedCount}
                shown={showSnoozed}
                onToggle={() => setShowSnoozed((v) => !v)}
              />
            ) : undefined
          }
        />
        {showSnoozed && <SnoozedList items={queue.snoozed} onRestore={onRestore} />}
      </div>
    )
  }

  return (
    <div data-part="OwnNextStepBanner">
      <NextStepBanner
        at="MY INVENTIONS"
        verb={first.verb}
        headline={first.headline}
        rule={first.rule}
        cta={{ label: first.cta.label, to: first.cta.to }}
        escape={
          onSnooze || onDrop ? (
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
              {onSnooze && (
                <span className="relative">
                  <button
                    type="button"
                    onClick={() => setShowSnoozeMenu((v) => !v)}
                    aria-expanded={showSnoozeMenu}
                    title="Snooze this item for 7 or 30 days"
                    className="font-bold text-iflow-700 hover:text-iflow-900"
                  >
                    Snooze ▾
                  </button>
                  {showSnoozeMenu && (
                    <span className="absolute right-0 top-5 z-10 flex flex-col rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                      {SNOOZE_DAY_OPTIONS.map((days) => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => {
                            setShowSnoozeMenu(false)
                            onSnooze(first, days)
                          }}
                          className="whitespace-nowrap px-3 py-1 text-left text-[11px] text-gray-700 hover:bg-iflow-50"
                        >
                          {days} days
                        </button>
                      ))}
                    </span>
                  )}
                </span>
              )}
              {onSnooze && onDrop && <span>·</span>}
              {onDrop && (
                <button
                  type="button"
                  onClick={() => onDrop(first)}
                  title="Drop this item from the queue"
                  className="font-bold text-iflow-700 hover:text-iflow-900"
                >
                  Not now
                </button>
              )}
            </div>
          ) : (
            <span className="text-[11px] text-gray-500">
              Snoozing needs your preferences, which have not loaded
            </span>
          )
        }
        queue={
          <span className="flex flex-wrap items-center gap-1.5">
            <span>
              {rest.length === 0
                ? 'Nothing else in your queue'
                : `${rest.length} more item${rest.length === 1 ? '' : 's'} in your queue`}
            </span>
            {rest.length > 0 && (
              <>
                <span>—</span>
                <button
                  type="button"
                  onClick={() => setShowQueue((v) => !v)}
                  aria-expanded={showQueue}
                  className="font-semibold text-iflow-600 hover:text-iflow-800"
                >
                  {showQueue ? 'Hide queue' : 'See queue'}
                </button>
              </>
            )}
            {snoozedCount > 0 && (
              <>
                <span className="text-gray-400">·</span>
                <SnoozedRow
                  count={snoozedCount}
                  shown={showSnoozed}
                  onToggle={() => setShowSnoozed((v) => !v)}
                />
              </>
            )}
          </span>
        }
      />
      {showQueue && rest.length > 0 && <QueueList items={rest} />}
      {showSnoozed && <SnoozedList items={queue.snoozed} onRestore={onRestore} />}
    </div>
  )
}

function SnoozedRow({ count, shown, onToggle }: { count: number; shown: boolean; onToggle: () => void }) {
  return (
    <span className="text-gray-500">
      {count} snoozed ·{' '}
      <button type="button" onClick={onToggle} className="font-semibold text-iflow-600 hover:text-iflow-800">
        {shown ? 'hide' : 'show'}
      </button>
    </span>
  )
}

/** The rest of the queue, so "See queue" leads to something rather than being
 * a link to the page you are already on. */
function QueueList({ items }: { items: OwnNextStepItem[] }) {
  return (
    <ul data-part="OwnNextStepQueue" className="mb-4 space-y-1">
      {items.map((item) => (
        <li key={item.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 px-3 py-1.5 text-[12px] text-gray-700">
          <span className="text-[10.5px] font-bold uppercase tracking-wide text-iflow-600">{item.verb}</span>
          <span className="min-w-0 flex-1">{item.headline}</span>
          <Link to={item.cta.to} className="shrink-0 font-semibold text-iflow-600 hover:text-iflow-800">
            {item.cta.label}
          </Link>
        </li>
      ))}
    </ul>
  )
}

function SnoozedList({
  items,
  onRestore,
}: {
  items: OwnNextStepItem[]
  onRestore?: (item: OwnNextStepItem) => void
}) {
  return (
    <ul data-part="OwnNextStepSnoozed" className="mb-4 space-y-1">
      {items.map((item) => (
        <li key={item.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 px-3 py-1.5 text-[12px] text-gray-500">
          <span className="min-w-0 flex-1">{item.headline}</span>
          {onRestore && (
            <button
              type="button"
              onClick={() => onRestore(item)}
              className="shrink-0 font-semibold text-iflow-600 hover:text-iflow-800"
            >
              Put it back
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}
